/**
 * Die Live-Verbindung (Kapitel 9).
 *
 * MQTT ueber WebSocket, mit einem Rueckfall auf REST. Die Bibliothek wird
 * **nachgeladen**: sie ist der groesste Brocken im Bündel, und die Karte
 * muss spielbar sein, bevor sie da ist (NFR-PERF-05). Bis dahin traegt der
 * Rueckfall.
 *
 * Der Rueckfall ist
 * nicht die Ausnahme, sondern der Normalfall an einer Messe: ein WLAN, in
 * dem achthundert Leute hängen, laesst eine WebSocket-Verbindung nicht
 * zuverlaessig stehen. Die Oberflaeche darf den Unterschied nicht zeigen,
 * ausser als Hinweiszeile.
 */
import type { MqttClient } from 'mqtt'

export type Handler = (topic: string, payload: Record<string, unknown>) => void
export type StateHandler = (state: LiveState) => void
export type LiveState = 'connecting' | 'live' | 'polling' | 'offline'

const PREFIX = 'bsb/v1'

export class Live {
  private client: MqttClient | null = null
  private timer: number | null = null
  private state: LiveState = 'offline'

  constructor(
    private readonly onMessage: Handler,
    private readonly onState: StateHandler,
    private readonly poll: () => Promise<void>,
  ) {}

  get connected(): boolean {
    return this.state === 'live'
  }

  private setState(next: LiveState): void {
    if (this.state === next) return
    this.state = next
    this.onState(next)
  }

  async connect(opts: { url: string; token: string; roomId: string; sessionId: string; gm?: boolean }): Promise<void> {
    this.disconnect()
    this.setState('connecting')
    // Der Rueckfall laeuft sofort an und wird erst abgeschaltet, wenn der
    // Broker wirklich antwortet. Sonst sieht der Spieler fuenf Sekunden
    // lang eine tote Karte.
    this.startPolling()

    try {
      const { default: mqtt } = await import('mqtt')
      const client = mqtt.connect(opts.url, {
        username: opts.sessionId,
        password: opts.token,
        clientId: `web-${opts.sessionId}-${Math.random().toString(36).slice(2, 8)}`,
        protocolVersion: 5,
        clean: false,
        reconnectPeriod: 3000,
        connectTimeout: 8000,
        keepalive: 30,
      })
      this.client = client

      const room = `${PREFIX}/rooms/${opts.roomId}`
      const topics = [
        `${room}/out/${opts.sessionId}/status`,
        `${room}/out/${opts.sessionId}/notice`,
        `${room}/state`,
        `${room}/pulse`,
        `${room}/board`,
        `${room}/events`,
      ]
      if (opts.gm) topics.push(`${room}/gm/feed`)

      client.on('connect', () => {
        client.subscribe(topics, { qos: 1 })
        this.setState('live')
        this.stopPolling()
      })
      client.on('message', (topic, payload) => {
        try {
          this.onMessage(topic, JSON.parse(payload.toString()) as Record<string, unknown>)
        } catch {
          /* Eine kaputte Nachricht ist kein Grund, die Verbindung zu beenden. */
        }
      })
      client.on('error', () => this.startPolling())
      client.on('close', () => this.startPolling())
      client.on('offline', () => this.startPolling())
    } catch {
      this.startPolling()
    }
  }

  publish(roomId: string, sessionId: string, leaf: string, payload: unknown): boolean {
    if (!this.client?.connected) return false
    this.client.publish(
      `${PREFIX}/rooms/${roomId}/${leaf.startsWith('gm/') ? leaf.replace('gm/', `gm/${sessionId}/`) : `in/${sessionId}/${leaf}`}`,
      JSON.stringify(payload),
      { qos: 1 },
    )
    return true
  }

  private startPolling(): void {
    if (this.timer !== null) return
    this.setState(navigator.onLine ? 'polling' : 'offline')
    const run = async () => {
      try {
        await this.poll()
        if (this.state !== 'live') this.setState('polling')
      } catch {
        this.setState('offline')
      }
    }
    void run()
    this.timer = window.setInterval(run, 5000)
  }

  private stopPolling(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer)
      this.timer = null
    }
  }

  disconnect(): void {
    this.stopPolling()
    if (this.client) {
      this.client.end(true)
      this.client = null
    }
    this.setState('offline')
  }
}
